import { Product, Venue, VenueReservation } from "@/graphql/operations";
import {
  ApolloError,
  LazyQueryExecFunction,
  MutationResult,
  QueryResult,
} from "@apollo/client";
import { Dispatch, SetStateAction, createContext } from "react";
import { EditVenueReservationMutationResponse } from "./mutations/EditVenueReservationAnswer";
import {
  GetVenueReservationQueryInput,
  GetVenueReservationQueryResponse,
} from "./queries/GetVenueReservationQuery";

type ReservationsContext = {
  reservationBounds: {
    earliestDate: Date | null;
    latestDate: Date | null;
    resolved: boolean;
  };
  acceptReservation: (reservationId: string, venueId?: string, venueName?: string) => Promise<void>;
  canFetchMorePastReservations: boolean;
  canFetchMoreUpcomingReservations: boolean;
  editVenueReservationAnswerMutationResponse: MutationResult<EditVenueReservationMutationResponse>;
  error: ApolloError | undefined;
  fetchReservations: (options?: {
    month?: Date;
    past?: number;
    future?: number;
  }) => Promise<void>;
  fetchMorePastReservations: () => Promise<void>;
  fetchMoreUpcomingReservations: () => Promise<void>;
  fetchReservation: LazyQueryExecFunction<
    GetVenueReservationQueryResponse,
    GetVenueReservationQueryInput
  >;
  fetchReservationResult: QueryResult<
    GetVenueReservationQueryResponse,
    GetVenueReservationQueryInput
  >;
  fetchInitialReservations: (
    overrideSearch?: { venueId?: string; productId?: string },
    baseDateOverride?: Date
  ) => Promise<void>;
  from: Date | null;
  fetchedMonthsRange: { from: Date | null; until: Date | null };
  hasSearchParams: boolean;
  loading: boolean;
  pendingReservationsCount: number;
  pendingReservations: VenueReservation[];
  reservationsStart: number;
  reservationsEnd: number;
  productId: Product["id"] | null;
  rejectReservation: (reservationId: string, venueId?: string) => Promise<void>;
  reservations: VenueReservation[];
  resetReservation: (
    reservationId: string,
    venuePortalUserId?: string,
  ) => Promise<void>;
  resetSearch: () => Promise<void>;
  setFrom: Dispatch<SetStateAction<Date | null>>;
  setProductId: Dispatch<SetStateAction<Product["id"] | null>>;
  setUntil: Dispatch<SetStateAction<Date | null>>;
  setVenueId: Dispatch<SetStateAction<Venue["id"] | null>>;
  totalReservations: number;
  until: Date | null;
  venueId: Venue["id"] | null;
  reservationsById: Record<string, VenueReservation>;
};

export const ReservationsContext = createContext<ReservationsContext | null>(
  null,
);
